// @ts-strict-ignore
import React, { useContext, useMemo } from 'react';
import AggregationSelect from './AggregationSelect';
import AggregationSearchableDropdown from './AggregationSearchableDropdown';
import {
  StyledAggregationSection,
  StyledAggregationCriteriaRow,
  StyledAggregationClause,
  StyledAggregationClausePrimary,
} from './styles';
import AggregationNumberInput from './AggregationNumberInput';
import { AggregationEditorContext } from './context';
import { RuleBuilderContext } from '../NodeEditorDrawer/ThresholdsTab/RuleBuilderContainer/context';
import {
  SCOPE_OPTIONS,
  TIMEFRAME_UNIT_OPTIONS,
} from './constants';
import type { AggregationSelectFieldKey } from './types';
import {
  buildOptionGroups,
  getAggregationMetadata,
  getColumnKey,
  getAggregationTypeOptions,
  getShowTimeframeFilter,
  getTimeframeMaxExceededMessage,
  reconcileAggregationType,
  sortAggregationFilterOptionsByLabel,
  toColumnOptions,
} from './helpers';
import {
  Icon, IconName, Tooltip, TooltipProvider, useSnackBar,
} from '@usealloy/component-library';
import type { AggregationType, TimeframeUnit } from 'flow/store/engine/constants';

const BaseAggregationsSection = () => {
  const {
    sourceData, setSourceData, multiSelectProps, aggregationConfig,
  } = useContext(AggregationEditorContext);
  const { containerType } = useContext(RuleBuilderContext);
  const addSnackBar = useSnackBar();

  const columnSlugOptions = useMemo(
    () => toColumnOptions(aggregationConfig.columns),
    [aggregationConfig.columns],
  );
  const columnSlugOptionsGrouped = useMemo(
    () => buildOptionGroups({
      options: sortAggregationFilterOptionsByLabel(columnSlugOptions),
      objectDisplayName: aggregationConfig.objectDisplayName,
    }),
    [columnSlugOptions, aggregationConfig.objectDisplayName],
  );

  const columnKey = getColumnKey(sourceData.aggregation);

  const aggregationTypeOptions = useMemo(
    () => getAggregationTypeOptions(columnSlugOptions, columnKey),
    [columnSlugOptions, columnKey],
  );
  const scopeOptionsWithTooltips = useMemo(
    () => SCOPE_OPTIONS.map(option => {
      const { tooltipText, ...rest } = option;
      if (!tooltipText) return option;
      return {
        ...rest,
        meta: { hideIcon: true },
        icon: (
          <span style={{ order: 1, marginLeft: 'auto', display: 'flex' }}>
            <TooltipProvider>
              <Tooltip delayDuration={0} content={tooltipText} side="right">
                <Icon
                  icon={IconName.Info}
                  width={12}
                  height={12}
                  tabIndex={0}
                  role="button"
                  aria-label={`Info about ${option.label}`}
                />
              </Tooltip>
            </TooltipProvider>
          </span>
        ),
      };
    }),
    [],
  );

  const handleSelectChange = (field: AggregationSelectFieldKey) => (
    { value }: { value: string },
  ) => {
    switch (field) {
      case 'timeframeUnit':
        setSourceData(prev => ({
          ...prev,
          [field]: value as TimeframeUnit,
          timeframeIntervalStart: 0,
          timeframeIntervalEnd: 0,
        }));
        break;
      case 'columnSlug': {
        const selected = columnSlugOptions.find(
          o => o.value === value,
        );
        setSourceData(prev => {
          // Make sure the showTimeframeFilter prop matches the actual
          // aggregationType.
          const reconciledAggregationType = reconcileAggregationType(
            columnSlugOptions, value, prev.aggregation.aggregationType,
          );
          return ({
            ...prev,
            aggregation: {
              columnSlug: selected.column_slug ?? value,
              aggregationType: reconciledAggregationType,
              ...getAggregationMetadata(selected),
            },
            showTimeframeFilter: getShowTimeframeFilter(
              reconciledAggregationType, aggregationConfig.objectType,
            ),
          });
        });
        break;
      }
      case 'aggregationType':
        setSourceData(prev => ({
          ...prev,
          showTimeframeFilter: getShowTimeframeFilter(
            value as AggregationType, aggregationConfig.objectType,
          ),
          aggregation: {
            ...prev.aggregation,
            aggregationType: value as AggregationType,
          },
        }));
        break;
      case 'scope':
        setSourceData(prev => ({ ...prev, [field]: value }));
        break;
    }
  };

  const handleTimeframeNumberChange = (field: 'timeframeIntervalStart' | 'timeframeIntervalEnd') => (newValue: number) => {
    setSourceData(prev => {
      if (field === 'timeframeIntervalEnd') {
        const message = getTimeframeMaxExceededMessage(prev.scope, prev.timeframeUnit, newValue);
        if (message) {
          addSnackBar({ title: message, type: 'danger' });
        }
      }
      return { ...prev, [field]: newValue };
    });
  };

  return (
    <StyledAggregationSection>
      <StyledAggregationCriteriaRow>
        <AggregationSelect
          id="agg-type"
          multiSelectProps={multiSelectProps}
          options={aggregationTypeOptions}
          value={sourceData.aggregation.aggregationType}
          onChange={handleSelectChange('aggregationType')}
        />
        <StyledAggregationClause containerType={containerType}>
          of
        </StyledAggregationClause>
        <AggregationSearchableDropdown
          mode="single"
          groups={columnSlugOptionsGrouped}
          value={columnKey}
          fallbackLabel={sourceData.aggregation.publishedAttributeMetadata?.publishedAttributeName}
          searchPlaceholder="Search columns"
          emptyMessage="No columns available."
          onSelect={handleSelectChange('columnSlug')}
        />
        <StyledAggregationClause containerType={containerType}>
          with the following criteria:
        </StyledAggregationClause>
      </StyledAggregationCriteriaRow>

      <StyledAggregationCriteriaRow>
        <StyledAggregationClause containerType={containerType}>
          Scope:
        </StyledAggregationClause>
        <AggregationSelect
          id="agg-scope"
          multiSelectProps={multiSelectProps}
          options={scopeOptionsWithTooltips}
          value={sourceData.scope}
          onChange={handleSelectChange('scope')}
        />
      </StyledAggregationCriteriaRow>
      <StyledAggregationCriteriaRow>
        <StyledAggregationClause containerType={containerType}>
          Timeframe:
        </StyledAggregationClause>
        {sourceData.showTimeframeFilter ? (
          <>
            <AggregationNumberInput
              minValue={0}
              maxValue={999}
              value={sourceData.timeframeIntervalStart}
              onHandleValueChange={handleTimeframeNumberChange('timeframeIntervalStart')}
            />
            <StyledAggregationClause containerType={containerType}>
              to
            </StyledAggregationClause>
            <AggregationNumberInput
              minValue={sourceData.timeframeIntervalStart}
              maxValue={Number.MAX_SAFE_INTEGER}
              value={sourceData.timeframeIntervalEnd}
              onHandleValueChange={handleTimeframeNumberChange('timeframeIntervalEnd')}
            />
            <AggregationSelect
              id="agg-timeframe-unit"
              multiSelectProps={multiSelectProps}
              options={TIMEFRAME_UNIT_OPTIONS}
              value={sourceData.timeframeUnit}
              onChange={handleSelectChange('timeframeUnit')}
            />
            <StyledAggregationClause containerType={containerType}>
              ago
            </StyledAggregationClause>
          </>
        ) : (
          <>
            <StyledAggregationClausePrimary containerType={containerType}>
              Entity&apos;s Latest Value
            </StyledAggregationClausePrimary>
            <Tooltip
              delayDuration={0}
              content="Custom timeframes are not available when aggregating with Average, Max, Min, or Sum"
              side="right"
            >
              <Icon
                icon={IconName.Info}
                width={12}
                height={12}
                tabIndex={0}
              />
            </Tooltip>
          </>
        )}
      </StyledAggregationCriteriaRow>
    </StyledAggregationSection>
  );
};

export default BaseAggregationsSection;
